import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import 'posthog-js/dist/exception-autocapture';
import 'posthog-js/dist/web-vitals';
import postHog, { type CaptureResult } from 'posthog-js/dist/module.no-external';
import useVersionsStore from '@/common/store/pinia/versions';
import useAuthStore from '@/common/store/pinia/auth';
import useInfoStore from '@/common/store/pinia/info';
import { store } from '@/common/root';
import { DOMAIN_PROPS_KEYS } from '@/common/constants/domainProps.constants';
import takeFirst from '@/common/utils/takeFirst';
import { DEFAULT_DOMAIN_NAME } from '@/common/constants/domains.constants';
import { setPersonProperties } from '@/common/store/pinia/telemetry/core/client';
import { isAutotestEnvironment } from '@/common/store/pinia/telemetry/core/env';
import { updateUser } from '@/common/store/pinia/telemetry/modules/user';
import {
  initializeMonitors,
  cleanupMonitors,
  getPostHogBaseConfig,
  getTelemetryApiKey,
} from '@/common/store/pinia/telemetry/modules/init';
import shouldFilterError, { isDuplicateChunkPreloadFailure } from '@/common/store/pinia/telemetry/modules/errorDenyList';
import appConfig from '@/common/appConfig';
import useThemeStore from '@/common/store/pinia/theme';

declare const APP_VERSION: string;

const useTelemetryStore = defineStore('telemetry', () => {
  const enabled = ref<boolean | null>(null);
  const eventBuffer = ref<(CaptureResult | null)[]>([]);
  const isProcessingBuffer = ref(false);
  const isInitialized = ref(false);
  const requestErrorCount = ref(0);
  const requestErrorWindowStart = ref(0);
  const MAX_REQUEST_ERRORS = 10;
  const REQUEST_ERROR_WINDOW_MS = 60_000;
  const TELEMETRY_ENABLED_ENVIRONMENTS = ['production', 'playwright'];
  const themeStore = useThemeStore();

  const isTelemetryEnvironmentEnabled = computed(() => {
    if (!appConfig.app_env || !TELEMETRY_ENABLED_ENVIRONMENTS.includes(appConfig.app_env)) {
      return false;
    }

    return themeStore.themeData.useTelemetry;
  });

  const canInitializeTelemetry = ref(isTelemetryEnvironmentEnabled.value
    && Boolean(appConfig.telemetry?.apiKey)
    && Boolean(appConfig.telemetry?.host));

  const fetchAndRegisterVersionData = async (): Promise<void> => {
    try {
      const versionsStore = useVersionsStore();
      const versionData = await takeFirst(versionsStore.fetchAbout);
      const storageRelease = versionData['storage-release'];
      const storageName = versionData['storage-name'];

      setPersonProperties({
        vz_frontend_version: APP_VERSION,
        vz_product_version: storageRelease?.version,
        vz_product_release: storageRelease?.release,
        vz_storage_name: storageName
      });
    } catch (err) {
      setPersonProperties({
        vz_frontend_version: APP_VERSION,
      });
    }
  };

  const setUserData = (): void => {
    const authStore = useAuthStore();
    updateUser(authStore.currentUser, {
      isSystemAdmin: authStore.isSystemAdmin,
      isDomainAdmin: authStore.isDomainAdmin,
      isProjectAdmin: authStore.isProjectAdmin
    });
  };

  const flushEventBuffer = (): void => {
    if (isProcessingBuffer.value) {
      return;
    }

    isProcessingBuffer.value = true;
    const eventsToFlush = [...eventBuffer.value];
    eventBuffer.value = [];

    eventsToFlush.forEach(event => {
      if (event) {
        postHog.capture(event.event, event.properties, {
          timestamp: event.timestamp,
          $set: event.$set,
          $set_once: event.$set_once
        });
      }
    });

    isProcessingBuffer.value = false;
  };

  const disable = (): void => {
    enabled.value = false;
    if (!isProcessingBuffer.value) {
      eventBuffer.value = [];
    }

    cleanupMonitors();
  };

  // WORKAROUND: Hard-kill PostHog when the endpoint is unreachable.
  // opt_out_capturing only blocks new captures — the internal retry queue
  // keeps replaying failed requests up to 10 times with exponential backoff.
  // Setting __loaded = false gates _send_request, so neither new captures nor
  // queued retries can leave the browser. Uses private fields of posthog-js
  // (see node_modules/posthog-js/dist/module.js _send_request, _retryQueue).
  const killPostHog = (): void => {
    const ph = postHog as unknown as {
      __loaded?: boolean;
      _retryQueue?: { unload?: () => void };
      _requestQueue?: { unload?: () => void };
    };
    // eslint-disable-next-line no-underscore-dangle
    ph.__loaded = false;
    // eslint-disable-next-line no-underscore-dangle
    ph._retryQueue?.unload?.();
    // eslint-disable-next-line no-underscore-dangle
    ph._requestQueue?.unload?.();
  };

  const enable = (): void => {
    enabled.value = true;
    fetchAndRegisterVersionData();
    setUserData();

    const authStore = useAuthStore();
    if (authStore.isLoggedIn) {
      const infoStore = useInfoStore();
      infoStore.fetchAndRegisterLicenseData();
    }

    flushEventBuffer();
    initializeMonitors();
  };

  const checkTelemetryEnabled = async (): Promise<boolean> => {
    try {
      if (isAutotestEnvironment()) {
        enable();
        return true;
      }

      const result = await store.$takeFirst('domainProps/FETCH_PROP', {
        domain: DEFAULT_DOMAIN_NAME,
        key: DOMAIN_PROPS_KEYS.TELEMETRY
      });

      const isEnabled = result?.enabled ?? false;

      if (isEnabled) {
        enable();
      } else {
        disable();
      }

      return isEnabled;
    } catch (error: unknown) {
      disable();
      return false;
    }
  };

  const onTelemetryLoaded = async (): Promise<void> => {
    postHog.capture('$pageview');
    await checkTelemetryEnabled();
  };

  const resetTelemetryState = (): void => {
    cleanupMonitors();
    enabled.value = null;
    eventBuffer.value = [];
    isProcessingBuffer.value = false;
    isInitialized.value = false;
    requestErrorCount.value = 0;
    requestErrorWindowStart.value = 0;
  };

  const initialize = (): void => {
    try {
      if (isInitialized.value || !canInitializeTelemetry.value) {
        return;
      }

      const apiKey = getTelemetryApiKey();
      const baseConfig = getPostHogBaseConfig();

      if (!apiKey || !baseConfig) {
        return;
      }

      postHog.init(apiKey, {
        ...baseConfig,
        loaded: onTelemetryLoaded,
        on_request_error: () => {
          const now = Date.now();
          // Only a burst of failures should kill PostHog. Reset the counter at
          // the start of each rolling window so sporadic, isolated errors never
          // accumulate toward the threshold — we act only when the endpoint is
          // consistently failing (MAX_REQUEST_ERRORS within REQUEST_ERROR_WINDOW_MS).
          if (now - requestErrorWindowStart.value > REQUEST_ERROR_WINDOW_MS) {
            requestErrorWindowStart.value = now;
            requestErrorCount.value = 0;
          }
          requestErrorCount.value += 1;

          if (requestErrorCount.value >= MAX_REQUEST_ERRORS) {
            disable();
            killPostHog();
          }
        },
        before_send: (event) => {
          if (!event) {
            return null;
          }

          if (shouldFilterError(event)) {
            return null;
          }

          if (enabled.value === null) {
            if (!isProcessingBuffer.value) {
              eventBuffer.value.push(event);
            }
            return null;
          }

          if (!enabled.value) {
            return null;
          }

          // Runs only here, on the send path: a buffered event passes through `before_send`
          // twice (once when captured, once when `flushEventBuffer` replays it), and a window
          // check applied on both passes would discard the replay as a duplicate of itself.
          if (isDuplicateChunkPreloadFailure(event)) {
            return null;
          }

          return event;
        }
      });

      isInitialized.value = true;
    } catch (error) {
      // Silently fail telemetry initialization
    }
  };

  const $reset = () => {
    resetTelemetryState();
  };

  return {
    enabled,
    eventBuffer,
    isProcessingBuffer,
    isInitialized,
    isTelemetryEnvironmentEnabled,
    canInitializeTelemetry,
    initialize,
    enable,
    disable,
    flushEventBuffer,
    checkTelemetryEnabled,
    resetTelemetryState,
    $reset
  };
});

export default useTelemetryStore;
